import { useMemo } from 'react';
import { Button, useQueryState, useMutation, useI18n, usePersistedCooldown } from '@ywwwtseng/react-kit';
import * as schema from '@/db/schema';
import { EMAIL_REGEX } from '@/lib/react-form';
import { useBlockedWhenServiceUpdated } from '@/hooks/useBlockedWhenServiceUpdated';

interface EmailOtpButtonProps {
  disabled?: boolean;
  action?: string;
  email?: string;
  onSuccess?: () => void;
}

export function EmailOtpButton({
  disabled,
  action,
  email,
  onSuccess,
}: EmailOtpButtonProps) {
  const me = useQueryState<typeof schema.users.$inferSelect>('me');
  const isValid = useMemo(() => {
    if (email === undefined) return true;
    return email && EMAIL_REGEX.test(email);
  }, [email]);

  const { t } = useI18n();
  const sendOtp = useMutation(action ? 'otp:request' : 'otp:email');
  const { isCoolingDown, remainingSeconds, startCooldown } = usePersistedCooldown(
    'email_otp',
    60
  );

  const { mutate, isLoading } = useBlockedWhenServiceUpdated(async () => {
    await sendOtp
      .mutate(
        action
          ? {
            action,
            locale: me?.language_code,
          }
          : {
            email,
            locale: me?.language_code,
          }
      )
      .then((result) => {
        if (result.ok) {
          onSuccess?.();
          startCooldown();
        }
      });
  });

  const label = useMemo(() => {
    if (isCoolingDown) {
      return t('common.resend_otp', { time: String(remainingSeconds) });
    }
    return t('common.send_otp');
  }, [isCoolingDown, remainingSeconds, t]);

  return (
    <Button
      className="w-full"
      isLoading={isLoading}
      disabled={!isValid || isCoolingDown || disabled}
      color="secondary"
      variant="contained"
      size="sm"
      rounded="lg"
      onClick={() => {
        void mutate();
      }}
    >
      {label}
    </Button>
  );
}
